import React, { useState, useEffect, useRef } from "react";
import CountUp from "react-countup";

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setIsVisible(true);
            setHasAnimated(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <section ref={sectionRef} id="stedprep-stats" className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 text-center justify-center">
        <h2 className="text-3xl lg:text-4xl font-semibold text-[#2D68C4] mb-10 mx-auto max-w-full">
          {`Ace the ISEE with Confidence`}
        </h2>

        <div className="flex flex-col md:flex-row text-center justify-center gap-8 md:gap-12 lg:gap-16 mb-10">
          <div className="flex flex-col items-center">
            <div className="relative text-[55px] md:text-[40px] lg:text-[52px] font-bold text-[#DA7D00] mb-1 leading-none">
              <span className="opacity-0">10,000+</span>
              <div className="absolute inset-0 flex items-center justify-center">
                {isVisible && (
                  <CountUp
                    start={0}
                    end={10000}
                    duration={1}
                    suffix="+"
                    separator=","
                    useEasing={true}
                    enableScrollSpy={false}
                  />
                )}
                {!isVisible && "0+"}
              </div>
            </div>
            <div className="text-[12px] md:text-[16px] text-gray-600">
              {`Practice Exercise`}
            </div>
          </div>

          <div className="flex flex-col items-center">
            <div className="relative text-[55px] md:text-[40px] lg:text-[52px] font-bold text-[#DA7D00] mb-1 leading-none">
              <span className="opacity-0">10+</span>
              <div className="absolute inset-0 flex items-center justify-center">
                {isVisible && (
                  <CountUp
                    start={0}
                    end={10}
                    duration={1}
                    suffix="+"
                    useEasing={true}
                    enableScrollSpy={false}
                  />
                )}
                {!isVisible && "0+"}
              </div>
            </div>
            <div className="text-[12px] md:text-[16px] text-gray-600">
              {`Full-Length Tests`}
            </div>
          </div>

          <div className="flex flex-col items-center">
            <div className="relative text-[55px] md:text-[40px] lg:text-[52px] font-bold text-[#DA7D00] mb-1 leading-none">
              <span className="opacity-0">100%</span>
              <div className="absolute inset-0 flex items-center justify-center">
                {isVisible && (
                  <CountUp
                    start={0}
                    end={100}
                    duration={1}
                    suffix="%"
                    useEasing={true}
                    enableScrollSpy={false}
                  />
                )}
                {!isVisible && "0%"}
              </div>
            </div>
            <div className="text-[12px] md:text-[16px] text-gray-600">
              {`Teacher Validated`}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 w-full my-0 lg:my-2"></div>
      </div>
    </section>
  );
};

export default StatsSection;
